import { type Product } from "@shopify/hydrogen-react/storefront-api-types";
import useSWR from "swr/immutable";

import getProductByIdQuery from "@graphql/get-product-by-id.graphql";
import {
  multiStorefrontAPIFetcher,
  storefrontAPIFetcher,
} from "@react/hooks/fetchers";
import { useSectionSettings } from "@react/sections/Root/SectionSettingsContext";
import { STOREFRONT_API_URL } from "@tablescape/constants";
import {
  type CartSectionSettings,
  type CartProduct,
  type CartProductConnection,
} from "@tablescape/state";
import { formatProduct } from "@tablescape/utilities";

type ProductConnection = {
  data?: {
    product: CartProductConnection;
  };
};

export const useProductParams = [STOREFRONT_API_URL, getProductByIdQuery];

export function useProduct(productId: Product["id"] | undefined) {
  const {
    meta: {
      localization: { country, language },
    },
  } = useSectionSettings<CartSectionSettings>();
  const { data } = useSWR<ProductConnection>(
    productId && country
      ? [...useProductParams, { productId, country, language }]
      : null,
    storefrontAPIFetcher,
    {}
  );

  if (data?.data?.product) {
    return {
      product: formatProduct(data.data.product),
    };
  }

  return {
    product: undefined,
  };
}

export function useProducts(productIds: Product["id"][] | undefined) {
  const {
    meta: {
      localization: { country, language },
    },
  } = useSectionSettings<CartSectionSettings>();
  const { data: aggregateData } = useSWR<ProductConnection[]>(
    productIds && country
      ? productIds.map((productId) => [
          ...useProductParams,
          { productId, country, language },
        ])
      : null,
    multiStorefrontAPIFetcher,
    {}
  );

  return {
    products: aggregateData
      ? aggregateData.reduce<CartProduct[]>(
          (acc, data) => [
            ...acc,
            ...(data?.data?.product ? [formatProduct(data.data.product)] : []),
          ],
          []
        )
      : [],
  };
}
